<template>
  <div class="space-y-8">
    <!-- Organizations Section -->
    <div class="space-y-6">
      <div>
        <h3 class="mb-2 text-lg font-medium">
          {{ t("settings.organizations") }}
        </h3>
        <p class="text-sm text-muted-foreground">
          {{ t("pages.organizations.description") }}
        </p>
      </div>

      <!-- Create organization button -->
      <div class="flex justify-start">
        <SettingsOrganizationCreateDialog @created="handleOrganizationCreated">
          <Button variant="outline">
            <Plus class="mr-2 h-4 w-4" />
            {{ t("pages.organizations.createNew") }}
          </Button>
        </SettingsOrganizationCreateDialog>
      </div>

      <div class="space-y-4" data-testid="organization-list">
        <ContextMenu v-for="org in organizations" :key="org.id">
          <ContextMenuTrigger as-child>
            <div
              class="flex items-center justify-between rounded-lg border p-4"
              :class="{
                'border-primary bg-primary/5': org.id === organizationId,
                'border-border': org.id !== organizationId,
              }"
              :data-testid="`organization-item-${normalizeName(org.name)}`"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10"
                >
                  <Building2 class="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div class="font-medium">{{ org.name }}</div>
                  <div
                    v-if="org.id === organizationId"
                    class="text-sm text-muted-foreground"
                  >
                    {{ t("pages.organizations.currentOrganization") }}
                  </div>
                </div>
              </div>

              <div class="flex items-center space-x-2">
                <Button
                  v-if="org.id !== organizationId"
                  variant="outline"
                  size="sm"
                  @click="switchToOrganization(org.id)"
                >
                  {{ t("pages.organizations.switch") }}
                </Button>
                <Button v-else variant="default" size="sm" disabled>
                  <Check class="mr-1 h-4 w-4" />
                  {{ t("pages.organizations.current") }}
                </Button>

                <SettingsOrganizationRenameDialog
                  v-if="org.role === 'ADMIN'"
                  :ref="(instance) => setRenameDialogRef(org.id, instance)"
                  :organization="org"
                  @renamed="handleOrganizationRenamed"
                >
                  <template #trigger="{ open }">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-muted-foreground hover:text-foreground"
                          @click="open()"
                        >
                          <Pencil class="mr-1 h-4 w-4" />
                          <span class="sr-only">{{
                            t("pages.organizations.rename")
                          }}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{{ t("pages.organizations.rename") }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </template>
                </SettingsOrganizationRenameDialog>

                <SettingsOrganizationCloneDialog
                  v-if="org.role === 'ADMIN'"
                  :ref="(instance) => setCloneDialogRef(org.id, instance)"
                  :organization="org"
                  @cloned="handleOrganizationCloned"
                >
                  <template #trigger="{ open }">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-muted-foreground hover:text-foreground"
                          @click="open()"
                        >
                          <Copy class="h-4 w-4" />
                          <span class="sr-only">{{
                            t("pages.organizations.clone")
                          }}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{{ t("pages.organizations.clone") }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </template>
                </SettingsOrganizationCloneDialog>

                <SettingsOrganizationDeleteDialog
                  v-if="org.role === 'ADMIN'"
                  :ref="(instance) => setDeleteDialogRef(org.id, instance)"
                  :organization="org"
                  @deleted="handleOrganizationDeleted"
                >
                  <template #trigger="{ open }">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-muted-foreground hover:text-foreground"
                          role="button"
                          @click="open()"
                        >
                          <Trash class="h-4 w-4" />
                          <span class="sr-only">{{
                            t("pages.organizations.delete")
                          }}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{{ t("pages.organizations.delete") }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </template>
                </SettingsOrganizationDeleteDialog>
              </div>
            </div>
          </ContextMenuTrigger>

          <ContextMenuContent>
            <ContextMenuItem
              inset
              :disabled="org.id === organizationId"
              @select="switchToOrganization(org.id)"
            >
              <ArrowRightLeft class="mr-2 h-4 w-4" />
              <span>
                {{
                  org.id === organizationId
                    ? t("pages.organizations.current")
                    : t("pages.organizations.switch")
                }}
              </span>
            </ContextMenuItem>
            <ContextMenuSeparator v-if="org.role === 'ADMIN'" />
            <ContextMenuItem
              v-if="org.role === 'ADMIN'"
              inset
              @select="renameDialogRefs.get(org.id)?.open()"
            >
              <Pencil class="mr-2 h-4 w-4" />
              <span>{{ t("pages.organizations.rename") }}</span>
            </ContextMenuItem>
            <ContextMenuItem
              v-if="org.role === 'ADMIN'"
              inset
              @select="cloneDialogRefs.get(org.id)?.open()"
            >
              <Copy class="mr-2 h-4 w-4" />
              <span>{{ t("pages.organizations.clone") }}</span>
            </ContextMenuItem>
            <ContextMenuItem
              v-if="org.role === 'ADMIN'"
              inset
              @select="deleteDialogRefs.get(org.id)?.open()"
            >
              <Trash class="mr-2 h-4 w-4" />
              <span>{{ t("pages.organizations.delete") }}</span>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        <div
          v-if="!organizations?.length"
          class="py-8 text-center text-muted-foreground"
        >
          <Building2 class="mx-auto mb-4 h-12 w-12 opacity-50" />
          <p>{{ t("pages.organizations.noOrganizations") }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ArrowRightLeft,
  Building2,
  Check,
  Copy,
  Pencil,
  Plus,
  Trash,
} from "lucide-vue-next";
import { useApiOrganizations } from "~/composables/api";
import type { ApiOrganization } from "~/server/api/organizations/index.get";

const { organizationId, setOrganizationId } = useOrganizationId();
const { data: organizations } = await useApiOrganizations();
const { t } = useI18n();

type DialogHandle = { open: () => void };

const renameDialogRefs = new Map<number, DialogHandle>();
const deleteDialogRefs = new Map<number, DialogHandle>();
const cloneDialogRefs = new Map<number, DialogHandle>();

const setRenameDialogRef = (id: number, instance: DialogHandle | null) => {
  if (instance) {
    renameDialogRefs.set(id, instance);
  } else {
    renameDialogRefs.delete(id);
  }
};

const setDeleteDialogRef = (id: number, instance: DialogHandle | null) => {
  if (instance) {
    deleteDialogRefs.set(id, instance);
  } else {
    deleteDialogRefs.delete(id);
  }
};

const setCloneDialogRef = (id: number, instance: DialogHandle | null) => {
  if (instance) {
    cloneDialogRefs.set(id, instance);
  } else {
    cloneDialogRefs.delete(id);
  }
};

const switchToOrganization = (orgId: number) => {
  if (orgId !== organizationId.value) {
    setOrganizationId(orgId);
  }
};

const handleOrganizationCreated = (newOrganizationId: number) => {
  setOrganizationId(newOrganizationId);
};

const handleOrganizationRenamed = (updated: ApiOrganization) => {
  const index = organizations.value?.findIndex((o) => o.id === updated.id);
  if (index !== undefined && index !== -1 && organizations.value) {
    organizations.value[index] = updated;
  }
};

const handleOrganizationDeleted = async (deletedId: number) => {
  if (organizations.value) {
    const idx = organizations.value.findIndex((o) => o.id === deletedId);
    if (idx !== -1) {
      organizations.value.splice(idx, 1);
    }
  }
  if (deletedId === organizationId.value && organizations.value?.length) {
    setOrganizationId(organizations.value[0].id);
  }
};

const handleOrganizationCloned = (cloned: ApiOrganization) => {
  setOrganizationId(cloned.id);
};

const normalizeName = (name: string) => {
  return name.toLowerCase().replaceAll(/[^0-9a-z]/gi, "-");
};
</script>
